import type { FC, ReactNode } from "react";
import type { ObjPathProxy } from "ts-object-path";
import type { MyPlannResourceDictionary } from "web_myplann/Infrastructure/Translations/ResourceDictionary";
import { useResource } from "web_myplann/Infrastructure/Translations/Resources";
import { BoxedSliderField } from "web_myplann/Inspiration/Components/BoxedSliderField";
import { InspirationSheet } from "web_myplann/Inspiration/Components/InspirationSheet";
import type { SliderBounds } from "web_myplann/Inspiration/Types/SliderBounds";
import { useResetStateOnOpen } from "web_myplann/Shared/Hooks/useResetStateOnOpen";

type ResourcePath = ObjPathProxy<MyPlannResourceDictionary, string>;

type Props = SliderBounds & {
	open: boolean;
	copy: ObjPathProxy<
		MyPlannResourceDictionary,
		{
			title: string;
			description: string;
			sliderLabel: string;
			submit: string;
		}
	>;
	descriptionValues?: Record<string, string>;
	hint?: ResourcePath;
	hintValues?: Record<string, string>;
	secondaryLabel?: ResourcePath;
	onSecondary?: () => void;
	initial: number;
	preventScrollOnClose?: boolean;
	onClose: () => void;
	onSubmit: (value: number) => void;
	children?: ReactNode;
};

export const InspirationSliderSheet: FC<Props> = ({
	open,
	copy,
	descriptionValues,
	hint,
	hintValues,
	secondaryLabel,
	onSecondary,
	initial,
	min,
	max,
	step,
	softMin,
	softMax,
	preventScrollOnClose,
	onClose,
	onSubmit,
	children,
}) => {
	const { t } = useResource();
	const [value, setValue] = useResetStateOnOpen(open, initial);

	return (
		<InspirationSheet
			open={open}
			preventScrollOnClose={preventScrollOnClose}
			onClose={onClose}
			title={t(copy.title)}
			description={t(copy.description, descriptionValues)}
			primaryLabel={t(copy.submit)}
			onPrimary={() => onSubmit(value)}
			secondaryLabel={secondaryLabel && t(secondaryLabel)}
			onSecondary={onSecondary}
		>
			<BoxedSliderField
				label={t(copy.sliderLabel)}
				value={value}
				onChange={setValue}
				min={min}
				max={max}
				step={step}
				softMin={softMin}
				softMax={softMax}
				hint={hint && t(hint, hintValues)}
			/>

			{children}
		</InspirationSheet>
	);
};
